import { Box, Link, List, ListItem, Typography } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownContentProps {
  content?: string | null;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => {
  if (!content) return null;

  return (
    <Box sx={{ maxWidth: '100%', '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        disallowedElements={['script', 'iframe', 'style']}
        components={{
          p: ({ children }) => (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography variant="h6" component="h2" sx={{ mt: 2.5, mb: 1.25, fontWeight: 700 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h6" component="h2" sx={{ mt: 2.5, mb: 1.25, fontWeight: 700 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle1" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
              {children}
            </Typography>
          ),
          ul: ({ children }) => <List dense disablePadding sx={{ mb: 1.5, pl: 1 }}>{children}</List>,
          ol: ({ children }) => <List disablePadding sx={{ mb: 1.5, pl: 1 }}>{children}</List>,
          li: ({ children }) => (
            <ListItem sx={{ display: 'list-item', py: 0.25, pl: 0.5 }}>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }} component="span">
                {children}
              </Typography>
            </ListItem>
          ),
          strong: ({ children }) => (
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {children}
            </Box>
          ),
          em: ({ children }) => (
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              {children}
            </Box>
          ),
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
              {children}
            </Link>
          ),
          table: ({ children }) => (
            <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                {children}
              </Box>
            </Box>
          ),
          th: ({ children }) => (
            <Box component="th" sx={{ textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2">{children}</Typography>
            </Box>
          ),
          td: ({ children }) => (
            <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                {children}
              </Typography>
            </Box>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default React.memo(MarkdownContent);
